/**
 * Manager view — final assessment scores for direct reports
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { penilaianApi } from '../api/penilaianApi'
import { RESULT_STATUS_LABELS, RESULT_STATUS_COLORS } from '../types/penilaian.types'

function ScoreCell({ score }: { score: number | null }) {
  if (score === null) return <span className="text-gray-300 italic text-sm">—</span>
  const color =
    score >= 90 ? 'text-green-700' :
    score >= 75 ? 'text-yellow-700' :
    score >= 60 ? 'text-orange-700' :
                  'text-red-700'
  return <span className={`font-semibold text-sm ${color}`}>{score.toFixed(1)}</span>
}

export default function TeamResultsPage() {
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')
  const navigate = useNavigate()

  const { data: periodsData } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  const { data: resultsData, isLoading } = useQuery({
    queryKey: ['team-results', selectedPeriodId],
    queryFn: () => penilaianApi.getTeamResults(selectedPeriodId ? { period_id: selectedPeriodId } : {}),
  })

  const periods = periodsData?.data ?? []
  const results = resultsData?.data ?? []

  // Auto-select the open period, or the most recent period if none is open
  useEffect(() => {
    if (selectedPeriodId || periods.length === 0) return
    const open = periods.find((p) => p.status === 'open')
    if (open) { setSelectedPeriodId(open.id); return }
    const sorted = [...periods].sort((a, b) =>
      b.year !== a.year ? b.year - a.year : b.quarter - a.quarter
    )
    if (sorted[0]) setSelectedPeriodId(sorted[0].id)
  }, [periods, selectedPeriodId])

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Nilai Tim Saya</h1>
        <p className="text-sm text-gray-500 mt-1">
          Hasil penilaian individu anggota tim yang sudah disetujui
        </p>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Pilih Periode</label>
        <select
          value={selectedPeriodId}
          onChange={(e) => setSelectedPeriodId(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
        >
          <option value="">— Semua periode —</option>
          {periods.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-gray-500">Memuat...</div>
      )}

      {!isLoading && results.length === 0 && (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
          <p className="text-gray-500">Belum ada hasil penilaian untuk tim Anda</p>
          <p className="text-sm text-gray-400 mt-1">
            Hasil akan muncul setelah HR menyetujui penilaian anggota tim
          </p>
        </div>
      )}

      {!isLoading && results.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Nama</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Departemen</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Periode</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-blue-500 uppercase tracking-wide">HS (70%)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-green-500 uppercase tracking-wide">SS (20%)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-purple-500 uppercase tracking-wide">AKHLAK (10%)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide">Nilai Akhir</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-blue-600 uppercase tracking-wide">Nilai HR</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {results.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/penilaian/results/${r.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {r.employee?.display_name ?? r.employee?.email ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {r.employee?.department ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {periods.find((p) => p.id === r.period_id)?.name ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ScoreCell score={r.hs_score} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ScoreCell score={r.ss_score} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ScoreCell score={r.akhlak_score} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    {r.final_score !== null ? (
                      <span className="text-base font-bold text-gray-900">{r.final_score.toFixed(1)}</span>
                    ) : (
                      <span className="text-gray-300 italic text-sm">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {r.hr_final_score != null ? (
                      <span className="text-base font-bold text-blue-600 tabular-nums">{r.hr_final_score.toFixed(1)}</span>
                    ) : (
                      <span className="text-gray-300 italic text-sm">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${RESULT_STATUS_COLORS[r.approval_status]}`}>
                      {RESULT_STATUS_LABELS[r.approval_status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
